import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ScheduleOutlined,
  TrendingUpOutlined,
  ShieldOutlined,
  LayersOutlined,
  AccountTreeOutlined,
  SmartToyOutlined,
  CheckRounded
} from '@mui/icons-material';
import Header from '../components/Header';
import Section from '../components/site/Section';
import SectionLabel from '../components/site/SectionLabel';
import LayerBlock from '../components/site/LayerBlock';
import SourcesDiagram from '../components/site/SourcesDiagram';
import FunctionFlowDiagram from '../components/site/FunctionFlowDiagram';
import AgentConversation from '../components/site/AgentConversation';

const STATS = [
  { number: '30–40%', text: 'of underwriter time spent on admin tasks like rekeying data' },
  { number: '69%', text: 'of reinsurance executives cite data quality as their top pain point' },
  { number: '50%', text: 'of reinsurance firms still rely on spreadsheets' }
];

const FUNCTION_CHIPS = [
  'Technical pricing',
  'Earned-to-incurred',
  'Portfolio reporting',
  'Late premium flags',
  'Insured performance',
  'Payment reconciliation',
  'Form completion',
  'Sanctions screening',
  'Risk data analysis',
  'Exposure monitoring'
];

const SUITE = [
  {
    Icon: LayersOutlined,
    name: 'The Data Workspace',
    text: 'Every system and file, standardised in one place.',
    href: '#layer-data'
  },
  {
    Icon: AccountTreeOutlined,
    name: 'The Function Builder',
    text: 'Any analysis or automation, built in plain English, run with one click.',
    href: '#layer-functions'
  },
  {
    Icon: SmartToyOutlined,
    name: 'The Decode Agent',
    text: 'An AI coworker that runs the work for you.',
    href: '#layer-agent'
  }
];

const WHO = [
  {
    name: 'Syndicates & managing agents',
    text: 'Bordereaux from every coverholder standardised on arrival, and a live view of the whole book.'
  },
  {
    name: 'MGAs',
    text: 'Policy, premium and claims data from every source in one place, and returns to capacity that build themselves.'
  },
  {
    name: 'Coverholders',
    text: 'Monthly premium and claims bordereaux produced from your own systems, in the format each capacity provider expects.'
  },
  {
    name: 'Brokers',
    text: 'Slips, schedules and loss runs read straight into a workspace, so placing and renewals start from clean data.'
  }
];

const STEPS = [
  {
    title: 'Send us a sample of your files',
    detail: 'A few bordereaux, a slip, a loss run. Whatever you have, in whatever format.'
  },
  {
    title: 'We show them in a Decode workspace',
    detail: 'Standardised and linked, on a call, within days.'
  },
  {
    title: 'Connect your systems',
    detail: 'Policy admin, claims, SharePoint, email. Nothing migrates.'
  },
  {
    title: 'Go live',
    detail: 'Your first functions built alongside your team, and the Agent from day one.'
  }
];

const AGENT_POINTS = [
  'Interrogate your book of business conversationally, question by question. It learns your book over time.',
  'Real multi-step work, end to end: analyse, build the report, send it.',
  'Every function your firm builds becomes a tool the Agent can pick up and use.'
];

const OUTCOMES = [
  {
    Icon: ScheduleOutlined,
    title: 'More time for new business',
    text: 'Repetitive data work runs itself, so underwriters underwrite.'
  },
  {
    Icon: TrendingUpOutlined,
    title: 'Better portfolio decisions',
    text: "A clear, current view of your whole book, not last month’s spreadsheet."
  },
  {
    Icon: ShieldOutlined,
    title: 'Nothing slips through',
    text: 'Late premiums, checks and errors caught automatically.'
  }
];

function LandingPage() {
  const navigate = useNavigate();

  useEffect(() => {
    const { hash } = window.location;
    if (hash) {
      document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="site">
      <Header />

      <section className="hero">
        <video
          className="hero__video"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster="/hero-poster.jpg"
        >
          <source src="/background.webm" type="video/webm" />
        </video>
        <div className="hero__overlay" />

        <div className="hero__content">
          <h1>
            The data workspace bringing
            <br />
            automation to the Lloyd&rsquo;s market
          </h1>

          <p className="hero__sub">
            Every system and file in one standardised workspace. One-click analytics and
            automations built over it. An AI coworker to run them.
          </p>

          <div className="hero__actions">
            <button type="button" className="btn btn--primary" onClick={() => navigate('/contact')}>
              Book a demo
            </button>
            <button type="button" className="btn btn--ghost" onClick={() => scrollTo('layer-data')}>
              See how it works
            </button>
          </div>

          <div className="suite" aria-label="The Decode suite">
            {SUITE.map(({ Icon, name, text, href }) => (
              <a
                key={name}
                href={href}
                className="suite__tile"
                onClick={(event) => {
                  event.preventDefault();
                  scrollTo(href.slice(1));
                }}
              >
                <span className="suite__icon" aria-hidden="true">
                  <Icon fontSize="inherit" />
                </span>
                <span className="suite__name">{name}</span>
                <span className="suite__text">{text}</span>
              </a>
            ))}
          </div>
        </div>
      </section>


      <Section id="who" tone="paper">
        <SectionLabel>Who it&rsquo;s for</SectionLabel>
        <h2>From coverholder to syndicate</h2>
        <div className="who">
          {WHO.map(({ name, text }) => (
            <div className="who__item" key={name}>
              <h3>{name}</h3>
              <p>{text}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section id="problem" tone="panel">
        <SectionLabel>The problem</SectionLabel>
        <h2 className="problem__title">
          Your firm&rsquo;s data lives in many places, and those places don&rsquo;t talk to each
          other.
        </h2>

        <div className="problem__diagram">
          <SourcesDiagram mode="broken" />
        </div>

        <p className="problem__line">
          One firm. Five versions of the truth. No single view of the book.
        </p>

        <div className="stat-row">
          {STATS.map((stat) => (
            <div className="stat" key={stat.number}>
              <div className="stat__number">{stat.number}</div>
              <p className="stat__text">{stat.text}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section id="product" tone="paper" className="band--intro">
        <SectionLabel>The product</SectionLabel>
        <h2>How Decode fits together</h2>
        <p className="layers__intro">
          Successful AI adoption needs three layers, built from the bottom up: unified data, defined
          workflows, and only then an AI that can act. Decode provides all three.
        </p>

        <nav className="layer-nav" aria-label="The three layers">
          <a href="#layer-data">01 &middot; The Data Workspace</a>
          <a href="#layer-functions">02 &middot; The Function Builder</a>
          <a href="#layer-agent">03 &middot; The Decode Agent</a>
        </nav>
      </Section>

      <Section id="layer-data" tone="panel" className="band--layer">
        <LayerBlock
          number="01"
          name="The Data Workspace"
          side="right"
          title="One workspace for every dataset and file"
          intro="Decode connects to the systems you already have, ingests the files you receive, and becomes the home for data that has no system of its own."
          points={[
            'Nothing needs migrating: Excel, SQL, SharePoint, Dropbox, email and data lakes connect as they are.',
            'Ingests any file, from slips and bordereaux to loss runs, applications and schedules, in Excel, PDF or Word.',
            "Every cleaning decision is traceable. When Decode isn’t sure, it asks rather than guesses."
          ]}
          diagram={<SourcesDiagram mode="connected" />}
        />
      </Section>

      <Section id="layer-functions" tone="paper" className="band--layer">
        <LayerBlock
          number="02"
          name="The Function Builder"
          side="left"
          title="One-click analytics and automations, built in plain English"
          intro="Centralising is not the goal. Using the data is. Describe the analysis you need, or upload the spreadsheet your team uses today, and Decode turns it into a one-click function over your data."
          points={[
            "A tested, auditable function: every run is logged and it’s shared firm-wide the moment it’s built.",
            'Runs on demand, on a schedule, or automatically whenever new data arrives.',
            'Integrates third-party services along the way, such as sanctions screening.'
          ]}
          diagram={<FunctionFlowDiagram />}
          footer={
            <div className="chips-strip">
              <span className="chips-label">What our customers build</span>
              <div className="chips">
                {FUNCTION_CHIPS.map((chip) => (
                  <span className="chip" key={chip}>
                    {chip}
                  </span>
                ))}
              </div>
            </div>
          }
        />
      </Section>

      <Section id="layer-agent" tone="navy" className="band--layer band--agent">
        <div className="agent-band__head">
          <div className="layer__head">
            <span className="layer__number">03</span>
            <SectionLabel className="layer__name">The Decode Agent</SectionLabel>
          </div>
          <h3>An AI coworker with your data and every function you&rsquo;ve built</h3>
          <p className="layer__intro">
            Assign whole tasks, in Decode or Microsoft Teams. The Decode Agent plans, runs your
            functions, and delivers the result.
          </p>
        </div>

        <div className="agent-band__stage">
          <AgentConversation />
        </div>

        <ul className="agent-band__points">
          {AGENT_POINTS.map((point) => (
            <li key={point}>
              <span className="layer__tick" aria-hidden="true">
                <CheckRounded fontSize="inherit" />
              </span>
              <span>{point}</span>
            </li>
          ))}
        </ul>
      </Section>

      <Section id="outcomes" tone="paper">
        <SectionLabel>What you get</SectionLabel>
        <h2>With everything in one place, repetitive work runs itself.</h2>

        <div className="outcomes">
          {OUTCOMES.map(({ Icon, title, text }) => (
            <div className="outcome" key={title}>
              <span className="outcome__disc" aria-hidden="true">
                <Icon fontSize="inherit" />
              </span>
              <h3>{title}</h3>
              <p>{text}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section id="cta" tone="panel" className="cta">
        <div className="cta__grid">
          <div className="cta__copy">
            <SectionLabel>Get started</SectionLabel>
            <h2>See Decode on your own data.</h2>
            <p>
              We&rsquo;ll walk through your files and systems and show you what Decode does with
              them.
            </p>
            <button type="button" className="btn btn--navy" onClick={() => navigate('/contact')}>
              Book a demo
            </button>
          </div>
          <div className="steps-card">
            <div className="steps-card__head">
              <SectionLabel>How we start</SectionLabel>
              <span className="steps-card__tag">Live in days, not months</span>
            </div>
            <ol className="steps">
              {STEPS.map(({ title, detail }, index) => (
                <li className="steps__item" key={title}>
                  <span className="steps__num" aria-hidden="true">
                    {index + 1}
                  </span>
                  <span className="steps__body">
                    <span className="steps__text">{title}</span>
                    <span className="steps__detail">{detail}</span>
                  </span>
                </li>
              ))}
            </ol>
            <img src="/logo-512.png" alt="" className="steps-card__mark" />
          </div>
        </div>
      </Section>
    </div>
  );
}

export default LandingPage;
